import React, { useEffect, useState } from 'react';
import { Button, Card, DatePicker, Select } from 'antd';
import { DownloadOutlined, LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import _ from 'lodash';
import { createApiGateway } from '../../../../apiGateway';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import { type SelectOption, type UsersyncStatisticsFilters } from '../../../../types/usersyncStatistics';

const apiGateway = createApiGateway();

const shortcutHint = ' (⌘/Ctrl+Enter)';

type BidderOption = {
  id: number;
  name: string;
};

interface UsersyncStatisticsFiltersProps {
  filters: UsersyncStatisticsFilters;
  onFiltersChange: (filters: Partial<UsersyncStatisticsFilters>) => void;
  onApplyFilters: () => void;
  loading: boolean;
  hasUnappliedChanges: boolean;
  onExportCsv?: () => void;
  exportDisabled?: boolean;
}

const groupByOptions: SelectOption[] = [
  { label: 'SSP', value: 'sspId' },
  { label: 'DSP', value: 'dspId' },
  { label: 'Stage', value: 'stage' },
  { label: 'Status', value: 'status' },
  { label: 'Reason', value: 'reason' },
  { label: 'Tag Type', value: 'tagType' },
];

const formatOptions: SelectOption[] = [
  { label: 'Summary', value: 'summary' },
  { label: 'By Day', value: 'by_day' },
  { label: 'By Hour', value: 'by_hour' },
];

const stageOptions: SelectOption[] = [
  { label: 'Initial Request', value: 'initial_request' },
  { label: 'Eligible For Sync', value: 'eligible_for_sync' },
  { label: 'Sync Attempt Generated', value: 'sync_attempt_generated' },
  { label: 'Callback Received', value: 'callback_received' },
  { label: 'Mapping Stored', value: 'mapping_stored' },
  { label: 'Drop', value: 'drop' },
];

const statusOptions: SelectOption[] = [
  { label: 'Success', value: 'success' },
  { label: 'Drop', value: 'drop' },
];

const reasonOptions: SelectOption[] = [
  { label: 'Missing SSP Id', value: 'missing_ssp_id' },
  { label: 'Invalid Redirect URL', value: 'invalid_redirect_url' },
  { label: 'SSP Not Found', value: 'ssp_not_found' },
  { label: 'No DSP Configured', value: 'no_dsp_configured' },
  { label: 'Mapping Exists', value: 'mapping_exists' },
  { label: 'DSP Not Found', value: 'dsp_not_found' },
  { label: 'Invalid Callback Query', value: 'invalid_callback_query' },
  { label: 'Missing Internal User Id', value: 'missing_internal_user_id' },
  { label: 'Storage Error', value: 'storage_error' },
];

const tagTypeOptions: SelectOption[] = [
  { label: 'Iframe', value: 'iframe' },
  { label: 'Image', value: 'image' },
  { label: 'Redirect', value: 'redirect' },
  { label: 'Script', value: 'script' },
];

export const UsersyncStatisticsFiltersComponent: React.FC<UsersyncStatisticsFiltersProps> = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  loading,
  hasUnappliedChanges,
  onExportCsv,
  exportDisabled,
}) => {
  const isMobile = useIsMobile();
  const [ssps, setSsps] = useState<BidderOption[]>([]);
  const [dsps, setDsps] = useState<BidderOption[]>([]);

  useEffect(() => {
    (async () => {
      const [sspsResponse, dspsResponse] = await Promise.all([
        apiGateway.ssps.getAll({ includeSpend: false }),
        apiGateway.dsps.getAll({ includeSpend: false }),
      ]);

      setSsps(sspsResponse);
      setDsps(dspsResponse);
    })();
  }, []);

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      onFiltersChange({
        dateRange: [dates[0].format('YYYY-MM-DD'), dates[1].format('YYYY-MM-DD')],
      });
    }
  };

  const handleSelectChange =
    (field: keyof UsersyncStatisticsFilters) => (value: string | number | (string | number)[] | null) => {
      onFiltersChange({
        [field]: Array.isArray(value) && value.length > 0 ? value : value || null,
      } as Partial<UsersyncStatisticsFilters>);
    };

  const sspOptions = _.sortBy(
    (ssps || []).map((ssp) => ({
      label: `[${ssp.id}] ${ssp.name}`,
      value: ssp.id,
    })),
    'value'
  );

  const dspOptions = _.sortBy(
    (dsps || []).map((dsp) => ({
      label: `[${dsp.id}] ${dsp.name}`,
      value: dsp.id,
    })),
    'value'
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !loading) {
        e.preventDefault();
        onApplyFilters();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onApplyFilters, loading]);

  return (
    <Card size="small" style={{ marginBottom: '12px', borderRadius: '6px' }} styles={{ body: { padding: '12px' } }}>
      <div
        className="xchange-filters"
        style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', flex: '1', minWidth: '0' }}>
          <DatePicker.RangePicker
            value={[dayjs(filters.dateRange[0]), dayjs(filters.dateRange[1])]}
            onChange={handleDateRangeChange}
            size="small"
            style={{ minWidth: '200px', flex: '0 0 auto' }}
          />

          <Select
            options={formatOptions}
            value={filters.format}
            onChange={handleSelectChange('format')}
            size="small"
            style={{ minWidth: '100px', width: '120px' }}
          />

          <Select
            mode="multiple"
            options={groupByOptions}
            value={filters.groupBy}
            onChange={handleSelectChange('groupBy')}
            placeholder="Group By"
            size="small"
            style={{ minWidth: '140px', width: '180px' }}
            allowClear
            maxTagCount="responsive"
          />

          <Select
            mode="multiple"
            options={sspOptions}
            value={filters.sspId as unknown as number[] | null}
            onChange={handleSelectChange('sspId')}
            placeholder="SSP"
            size="small"
            style={{ minWidth: '140px', width: '180px' }}
            allowClear
            showSearch
            filterOption={(input, option) =>
              ((option?.label as string) || '').toLowerCase().includes(input.toLowerCase())
            }
          />

          <Select
            mode="multiple"
            options={dspOptions}
            value={filters.dspId as unknown as number[] | null}
            onChange={handleSelectChange('dspId')}
            placeholder="DSP"
            size="small"
            style={{ minWidth: '140px', width: '180px' }}
            allowClear
            showSearch
            filterOption={(input, option) =>
              ((option?.label as string) || '').toLowerCase().includes(input.toLowerCase())
            }
          />

          <Select
            mode="multiple"
            options={stageOptions}
            value={filters.stage}
            onChange={handleSelectChange('stage')}
            placeholder="Stage"
            size="small"
            style={{ minWidth: '160px', width: '200px' }}
            allowClear
            maxTagCount="responsive"
          />

          <Select
            mode="multiple"
            options={statusOptions}
            value={filters.status}
            onChange={handleSelectChange('status')}
            placeholder="Status"
            size="small"
            style={{ minWidth: '120px', width: '150px' }}
            allowClear
            maxTagCount="responsive"
          />

          <Select
            mode="multiple"
            options={reasonOptions}
            value={filters.reason}
            onChange={handleSelectChange('reason')}
            placeholder="Reason"
            size="small"
            style={{ minWidth: '180px', width: '240px' }}
            allowClear
            maxTagCount="responsive"
          />

          <Select
            mode="multiple"
            options={tagTypeOptions}
            value={filters.tagType}
            onChange={handleSelectChange('tagType')}
            placeholder="Tag Type"
            size="small"
            style={{ minWidth: '140px', width: '180px' }}
            allowClear
            maxTagCount="responsive"
          />
        </div>

        <div style={{ flexShrink: 0, marginLeft: '8px', display: 'flex', gap: '8px' }}>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={onExportCsv}
            disabled={loading || exportDisabled}
            style={{ minWidth: '110px' }}
          >
            Export CSV
          </Button>
          <Button
            type="primary"
            size="small"
            icon={loading ? <LoadingOutlined /> : <SearchOutlined />}
            onClick={onApplyFilters}
            loading={loading}
            style={{ minWidth: '120px' }}
          >
            {loading
              ? 'Fetching...'
              : `${hasUnappliedChanges ? 'Fetch Statistics' : 'Up to Date'}${isMobile ? '' : shortcutHint}`}
          </Button>
        </div>
      </div>
    </Card>
  );
};
